import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  GuestLandingService,
  GuestMenuFulfilment,
  PublicMenuCategory,
  PublicMenuItemDetail,
  PublicMenuItemSummary,
  PublicTableAccessResponse,
} from '../guest-landing/guest-landing.service';

/**
 * Discriminated union surfaced to subscribers. Lets the UI handle
 * loading/error/gone/ok with a single subject contract instead of
 * juggling separate "value" and "status" feeds.
 */
export type CacheResult<T> =
  | { status: 'pending' }
  | { status: 'ok'; data: T }
  | { status: 'gone' }
  | { status: 'error' };

/**
 * TCWARM-1 — does this token offer the built-in menu at all? Not when the
 * server says the menu is off (`menuEnabled: false`: the store switched it
 * off, or its external-menu mode is on), and not when the server sends the
 * store's own menu link: the landing and bill screens then show that link
 * INSTEAD of the built-in menu (TC-G5c), so nothing on the guest's screens
 * leads to it. `menuEnabled` missing (an older backend) reads as on.
 */
export function builtInMenuOffered(access: PublicTableAccessResponse): boolean {
  return access.menuEnabled !== false && !access.externalMenuUrl?.trim();
}

/**
 * TC-MS8 — Reactive menu cache. Owns ALL API calls for the four
 * cacheable layers (token, categories, items, item details).
 *
 * Architecture:
 *   - Each (token, layer) pair has its own BehaviorSubject. Subscribers
 *     get the latest value immediately and pushed updates on every
 *     refresh — no per-component refetch logic.
 *   - bind(token) is the single bootstrap point. It seeds the subjects
 *     from sessionStorage (warm start), fires the initial fetches, and
 *     starts a TTL-cadence interval. Idempotent and safe to call from
 *     any component.
 *   - The TTL interval refreshes the token, categories and items
 *     layers in the background, independent of which screen the guest
 *     is currently looking at. Silent — emits to subjects only when
 *     content actually changed.
 *   - Components never touch GuestLandingService for these layers
 *     directly. They subscribe to token$/categories$/items$/itemDetail$
 *     and treat the cache as the source of truth.
 *
 * TCWARM-1 — two rules keep a guest's phone from pulling the whole menu:
 *   - The built-in menu (categories + items) is only read once a token
 *     resolve says it is offered ({@link builtInMenuOffered}). bind() is
 *     called from the landing, bill, cart and checkout screens, so before
 *     this a pay-at-table guest on a store whose menu is off (or hosted
 *     elsewhere) still downloaded all of it.
 *   - Item details load on demand: when the guest opens an item. There
 *     is no warm-up. It used to fetch every item's detail after each
 *     items load (704 calls per guest on a 704-item menu, 55% of the
 *     production API's time at lunch on 2026-10-25), and again whenever
 *     the 2-minute storage TTL had lapsed.
 *
 * Storage: sessionStorage with a 2-min TTL stamp for cold restarts
 * within the same tab session. Failures (quota, private mode, sandbox)
 * are swallowed — caching is a perf optimisation, never a correctness
 * requirement.
 *
 * OMNI-2a — fulfilment scope. The server filters the menu by sales channel,
 * and a delivery guest on a pickup token reads the delivery channel's menu
 * (`fulfilment=delivery`, G-8). The token's current scope is set by the menu
 * screen through {@link setFulfilment}; every categories / items / detail
 * read uses it, the sessionStorage keys carry it (the default scope keeps
 * the pre-OMNI keys byte-identical), and a response is only pushed to the
 * feeds when it was fetched under the scope that is still current — so a
 * pickup list can never land on a delivery screen or the other way round.
 * The menu screen only ever sets `'delivery'` when the tenant's channel
 * availability flag is on (F3); with it off the scope stays null and every
 * request, storage key and in-flight key is exactly the pre-OMNI one.
 *
 * TCVIS-1 — catch-up refresh when the tab comes back. The menu's image links
 * are S3 presigned GETs with a 5-minute life, re-signed by each items refresh.
 * The TTL tick skips while the tab is hidden, so a guest who comes back after
 * more than ~3 minutes away holds expired links until the next tick (up to
 * 2 minutes): list thumbnails 403 and stay empty, and the detail sheet's
 * warm-up skips its image. On `visibilitychange` to visible, a bound token
 * whose last refresh is a full TTL old (so a tick came due while hidden and
 * was skipped) refreshes at once, and its timer restarts from that refresh.
 * A short hide/show sends nothing, and the made-up tick replaces the skipped
 * one rather than adding to it: the old-phase tick never fires.
 */
@Injectable({ providedIn: 'root' })
export class MenuCacheService implements OnDestroy {
  /** TTL/refresh-cadence in milliseconds. The interval inside bind()
   *  uses the same value so cache freshness and visible refresh
   *  cadence stay in sync. */
  static readonly TTL_MS = 2 * 60 * 1000;

  private guest = inject(GuestLandingService);

  // ── Reactive state, one BehaviorSubject per (token, layer) ─────
  private tokenState = new Map<string, BehaviorSubject<CacheResult<PublicTableAccessResponse>>>();
  private categoriesState = new Map<string, BehaviorSubject<PublicMenuCategory[] | null>>();
  private itemsState = new Map<string, BehaviorSubject<PublicMenuItemSummary[] | null>>();
  /** Keyed `${token}:${itemId}` so multiple items can coexist. */
  private detailState = new Map<string, BehaviorSubject<PublicMenuItemDetail | null>>();

  /** One periodic-refresh timer per bound token. Presence in this map
   *  is the "is this token bound?" check. */
  private refreshTimers = new Map<string, ReturnType<typeof setInterval>>();

  /** TCVIS-1 — `Date.now()` of each bound token's last refresh round (bind,
   *  TTL tick, or catch-up on return). Every round sits on the timer's phase,
   *  so a gap of a full TTL means a tick was skipped while hidden. */
  private lastRefreshAt = new Map<string, number>();

  /** OMNI-2a — current menu fulfilment per token. Absent = `null` (dine-in / pickup). */
  private fulfilmentByToken = new Map<string, GuestMenuFulfilment>();

  /** OMNI-2a — TC-2: per `${token}:${itemId}`, true once that item's detail
   *  read answered not-available (404) under the token's current scope. */
  private detailNotAvailableState = new Map<string, BehaviorSubject<boolean>>();

  /** In-flight set — prevents duplicate concurrent refreshes for the
   *  same (token, layer). Keys: `tok:<token>`, `cats:<token>`,
   *  `items:<token>`, `det:<token>:<itemId>` — byte-identical to pre-OMNI
   *  for the default scope; the delivery scope appends `:delivery`
   *  (OMNI-2a, see {@link scopedInflightKey}). */
  private inFlight = new Set<string>();

  /** TCVIS-1 — one listener for every bound token. Leading edge only: the
   *  round it sends stamps {@link lastRefreshAt}, so a burst of visible
   *  events (app-switcher flicker) sends one round at most. */
  private readonly onVisibilityChange = (): void => {
    if (typeof document === 'undefined' || document.hidden) return;
    const now = Date.now();
    for (const token of [...this.refreshTimers.keys()]) {
      if (now - (this.lastRefreshAt.get(token) ?? 0) < MenuCacheService.TTL_MS) continue;
      this.refreshBound(token);
      this.startRefreshTimer(token);
    }
  };

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    for (const timer of this.refreshTimers.values()) clearInterval(timer);
    this.refreshTimers.clear();
  }

  // ── Bootstrap ──────────────────────────────────────────────────
  /**
   * Bootstrap caching for a publicToken. Call this from any
   * component that knows the token (typically the landing screen,
   * but also called defensively by menu/bill/cart in case they were
   * reached directly via deep link / refresh).
   *
   * Idempotent — the first call starts initial fetches and the TTL
   * interval; subsequent calls for the same token are no-ops.
   */
  bind(token: string): void {
    if (!token || this.refreshTimers.has(token)) return;

    // Seed subjects from sessionStorage so any immediate subscriber
    // gets a hot value rather than waiting for the network.
    this.ensureTokenSubject(token);
    this.ensureCategoriesSubject(token);
    this.ensureItemsSubject(token);

    this.startRefreshTimer(token);

    // Initial fetches.
    this.refreshBound(token);
  }

  /**
   * Periodic refresh — runs regardless of which screen is mounted.
   * document.hidden guard skips refreshes while the tab is in the
   * background (battery + bandwidth). TCVIS-1 — when the tab comes back
   * after a skipped tick, {@link onVisibilityChange} refreshes at once and
   * calls this again, so the cadence restarts from that refresh. Item
   * details are not refreshed here: they load when the guest opens an item.
   */
  private startRefreshTimer(token: string): void {
    const previous = this.refreshTimers.get(token);
    if (previous !== undefined) clearInterval(previous);
    this.refreshTimers.set(token, setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      this.refreshBound(token);
    }, MenuCacheService.TTL_MS));
  }

  /** One refresh round for a bound token: the resolve, plus the menu when
   *  the token is already known to have it on. */
  private refreshBound(token: string): void {
    // TCWARM-1 — taken before the resolve is sent: only a token already
    // known (warm from storage, resolved by bindTokenOnly, or by an earlier
    // round) to have its menu on fetches the menu alongside the resolve.
    // Otherwise the resolve's answer starts the menu fetch (see refreshToken).
    const menuWasOpen = this.menuOpen(token);
    this.lastRefreshAt.set(token, Date.now());
    this.refreshToken(token);
    if (menuWasOpen) this.refreshMenu(token);
  }

  /**
   * Lightweight bind for callers that only need the token resolve
   * (not categories + items + details).
   *
   * Used by {@link TcThemeService} which reads
   * `storeThemePrimaryColor` from the resolve response and applies it
   * to the document root. The theme service runs on every TableConnect
   * route navigation (via AppComponent's router-events subscription),
   * including `/payment-result` and `/receipt` where the menu cache
   * is genuinely unnecessary. Going through full `bind` there fires
   * three HTTP calls (`refreshToken` + `refreshCategories` +
   * `refreshItems`) on a page that consumes none of the latter two —
   * pure waste at best, and three loud 404s in the console when the
   * resolve itself 404s (master-switch off, store inactive, etc.).
   *
   * **Idempotency.** Calls `refreshToken` directly; that method has
   * its own in-flight guard, so duplicate calls are harmless. We
   * deliberately do NOT touch `refreshTimers` here — if a menu-data
   * page later calls full `bind()`, that call proceeds as normal and
   * spins up the periodic timer (which then refreshes the token feed
   * + categories + items on the usual cadence). So a guest who lands
   * deep at `/payment-result` and then navigates to `/menu` gets the
   * full cache spun up correctly on arrival.
   *
   * **No periodic refresh.** Token-only consumers don't need polling
   * — if the admin changes the theme colour mid-session, the guest's
   * next page that uses full `bind()` will pick it up. The theme is
   * "best-effort current" not "live."
   */
  bindTokenOnly(token: string): void {
    if (!token) return;
    this.ensureTokenSubject(token);
    this.refreshToken(token);
  }

  /**
   * OMNI-2a — set the fulfilment the menu is read under for this token.
   *
   * No-op when unchanged, so it is safe to call on every init and every mode
   * signal emission. On a real change the categories / items / detail feeds
   * are re-seeded from the NEW scope's sessionStorage (or `null` = pending,
   * which the menu screen treats as "keep what is on screen until the answer
   * lands"), and — when the token is bound and its menu is on — categories
   * and items are re-fetched at once. Item details re-read on demand: an item
   * open on screen at once, the rest on their next open. When the token is
   * not bound yet, the next `bind()` fetches under this scope.
   */
  setFulfilment(token: string, fulfilment: GuestMenuFulfilment): void {
    if (!token) return;
    const next: GuestMenuFulfilment = fulfilment === 'delivery' ? 'delivery' : null;
    if (this.scopeOf(token) === next) return;
    if (next === null) this.fulfilmentByToken.delete(token);
    else this.fulfilmentByToken.set(token, next);

    this.categoriesState.get(token)?.next(this.read<PublicMenuCategory[]>(this.kCats(token)));
    this.itemsState.get(token)?.next(this.read<PublicMenuItemSummary[]>(this.kItems(token)));
    const prefix = `${token}:`;
    // TC-2 — a 404 under the old scope says nothing about the new one.
    // Cleared before the re-reads below, so their own answer stands.
    for (const [key, subject] of this.detailNotAvailableState) {
      if (key.startsWith(prefix) && subject.value) subject.next(false);
    }
    for (const [key, subject] of this.detailState) {
      if (!key.startsWith(prefix)) continue;
      const itemId = key.slice(prefix.length);
      const seeded = this.read<PublicMenuItemDetail>(this.kDetail(token, itemId));
      subject.next(seeded);
      // TCWARM-1 — no warm-up re-reads details any more, so an item the
      // guest has open re-reads under the new scope now.
      if (!seeded && subject.observed) this.refreshDetail(token, itemId);
    }

    if (this.refreshTimers.has(token)) this.refreshMenu(token);
  }

  /** OMNI-2a — the fulfilment scope the token's menu is currently read under. */
  private scopeOf(token: string): GuestMenuFulfilment {
    return this.fulfilmentByToken.get(token) ?? null;
  }

  // ── Public reactive read API ───────────────────────────────────
  /** Token-resolve feed. Emits CacheResult so the UI can react to
   *  pending/ok/gone/error in a single switch. */
  token$(token: string): Observable<CacheResult<PublicTableAccessResponse>> {
    return this.ensureTokenSubject(token).asObservable();
  }

  /** Categories feed. null = pending, [] = empty store, [...] = ready. */
  categories$(token: string): Observable<PublicMenuCategory[] | null> {
    return this.ensureCategoriesSubject(token).asObservable();
  }

  /** Items feed. null = pending, [...] = ready (possibly empty). */
  items$(token: string): Observable<PublicMenuItemSummary[] | null> {
    return this.ensureItemsSubject(token).asObservable();
  }

  /** Item-detail feed, called once per open of the item. null =
   *  pending, otherwise the detail object.
   *
   *  TCWARM-1 — this is the only place a detail is fetched. An open
   *  with a fresh copy in storage (inside the TTL) costs nothing; any
   *  other open asks the server, while a copy already in memory keeps
   *  showing until the answer lands (the sheet keeps the guest's
   *  selections across that update). */
  itemDetail$(token: string, itemId: string): Observable<PublicMenuItemDetail | null> {
    const subject = this.ensureDetailSubject(token, itemId);
    if (!this.read<PublicMenuItemDetail>(this.kDetail(token, itemId))) {
      // TC-2 — a new ask, so the last 404 is not replayed as this open's answer.
      const notAvailable = this.detailNotAvailableState.get(`${token}:${itemId}`);
      if (notAvailable?.value) notAvailable.next(false);
      this.refreshDetail(token, itemId);
    }
    return subject.asObservable();
  }

  /**
   * OMNI-2a — TC-2: true when the item's detail read answered not-available
   * (a 404: the item is gone, or not sold on the guest's channel), so the
   * screen can say so instead of silently never opening the sheet. Replays the
   * latest answer (a BehaviorSubject), so subscribing after
   * {@link itemDetail$} already kicked the read never misses it. Flips back to
   * false when a later read of the item succeeds.
   */
  itemDetailNotAvailable$(token: string, itemId: string): Observable<boolean> {
    return this.ensureDetailNotAvailableSubject(token, itemId).asObservable();
  }

  // ── Subject lazy-init (hydrates from sessionStorage) ───────────
  private ensureTokenSubject(token: string): BehaviorSubject<CacheResult<PublicTableAccessResponse>> {
    let s = this.tokenState.get(token);
    if (!s) {
      const cached = this.read<PublicTableAccessResponse>(this.kToken(token));
      const initial: CacheResult<PublicTableAccessResponse> = cached
        ? { status: 'ok', data: cached }
        : { status: 'pending' };
      s = new BehaviorSubject<CacheResult<PublicTableAccessResponse>>(initial);
      this.tokenState.set(token, s);
    }
    return s;
  }

  private ensureCategoriesSubject(token: string): BehaviorSubject<PublicMenuCategory[] | null> {
    let s = this.categoriesState.get(token);
    if (!s) {
      s = new BehaviorSubject<PublicMenuCategory[] | null>(this.read<PublicMenuCategory[]>(this.kCats(token)));
      this.categoriesState.set(token, s);
    }
    return s;
  }

  private ensureItemsSubject(token: string): BehaviorSubject<PublicMenuItemSummary[] | null> {
    let s = this.itemsState.get(token);
    if (!s) {
      s = new BehaviorSubject<PublicMenuItemSummary[] | null>(this.read<PublicMenuItemSummary[]>(this.kItems(token)));
      this.itemsState.set(token, s);
    }
    return s;
  }

  private ensureDetailNotAvailableSubject(token: string, itemId: string): BehaviorSubject<boolean> {
    const key = `${token}:${itemId}`;
    let s = this.detailNotAvailableState.get(key);
    if (!s) {
      s = new BehaviorSubject<boolean>(false);
      this.detailNotAvailableState.set(key, s);
    }
    return s;
  }

  private ensureDetailSubject(token: string, itemId: string): BehaviorSubject<PublicMenuItemDetail | null> {
    const key = `${token}:${itemId}`;
    let s = this.detailState.get(key);
    if (!s) {
      s = new BehaviorSubject<PublicMenuItemDetail | null>(
        this.read<PublicMenuItemDetail>(this.kDetail(token, itemId)));
      this.detailState.set(key, s);
    }
    return s;
  }

  // ── Refreshers — every API call lives here, nowhere else ──────
  private refreshToken(token: string): void {
    const inflightKey = `tok:${token}`;
    if (this.inFlight.has(inflightKey)) return;
    this.inFlight.add(inflightKey);

    this.guest.resolveToken(token).subscribe({
      next: r => {
        this.inFlight.delete(inflightKey);
        const subject = this.ensureTokenSubject(token);
        if (r.ok) {
          const menuWasOpen = this.menuOpen(token);
          this.write(this.kToken(token), r.data);
          subject.next({ status: 'ok', data: r.data });
          // TCWARM-1 — the answer that first says the menu is on starts the
          // menu fetch for a bound token; the TTL tick keeps it fresh after.
          if (!menuWasOpen && this.refreshTimers.has(token)) this.refreshMenu(token);
        } else if (r.kind === 'not-available') {
          subject.next({ status: 'gone' });
        } else if (subject.value.status === 'pending') {
          // Only surface 'error' from a cold start. A transient
          // network blip during periodic refresh shouldn't tear
          // down a previously-ok view.
          subject.next({ status: 'error' });
        }
      },
      error: () => {
        this.inFlight.delete(inflightKey);
        const subject = this.ensureTokenSubject(token);
        if (subject.value.status === 'pending') subject.next({ status: 'error' });
      },
    });
  }

  private refreshCategories(token: string): void {
    // OMNI-2a — the scope is part of the in-flight key, so a request for the
    // previous fulfilment never blocks the one for the new fulfilment.
    const scope = this.scopeOf(token);
    const inflightKey = this.scopedInflightKey(`cats:${token}`, scope);
    if (this.inFlight.has(inflightKey)) return;
    this.inFlight.add(inflightKey);

    this.guest.resolveMenuCategories(token, scope).subscribe({
      next: r => {
        this.inFlight.delete(inflightKey);
        if (!r.ok) return;
        this.write(this.kCats(token, scope), r.data);
        // OMNI-2a — stored under the scope it was fetched for, but only
        // shown if the guest is still in that scope.
        if (this.scopeOf(token) !== scope) return;
        const subject = this.ensureCategoriesSubject(token);
        if (!this.categoriesEqual(subject.value, r.data)) {
          subject.next(r.data);
        }
      },
      error: () => { this.inFlight.delete(inflightKey); },
    });
  }

  private refreshItems(token: string): void {
    const scope = this.scopeOf(token); // OMNI-2a — see refreshCategories.
    const inflightKey = this.scopedInflightKey(`items:${token}`, scope);
    if (this.inFlight.has(inflightKey)) return;
    this.inFlight.add(inflightKey);

    this.guest.resolveMenuItems(token, null, scope).subscribe({
      next: r => {
        this.inFlight.delete(inflightKey);
        if (!r.ok) return;
        this.write(this.kItems(token, scope), r.data);
        if (this.scopeOf(token) !== scope) return;
        const subject = this.ensureItemsSubject(token);
        // Diff: only push a new emission if rendered fields actually
        // changed. Saves change-detection churn on no-op refreshes.
        if (!this.itemsEqual(subject.value, r.data)) {
          subject.next(r.data);
        }
        // TCWARM-1 — no detail warm-up from here: see itemDetail$.
      },
      error: () => { this.inFlight.delete(inflightKey); },
    });
  }

  private refreshDetail(token: string, itemId: string): void {
    const scope = this.scopeOf(token); // OMNI-2a — see refreshCategories.
    const inflightKey = this.scopedInflightKey(`det:${token}:${itemId}`, scope);
    if (this.inFlight.has(inflightKey)) return;
    this.inFlight.add(inflightKey);

    this.guest.resolveMenuItemDetail(token, itemId, scope).subscribe({
      next: r => {
        this.inFlight.delete(inflightKey);
        if (!r.ok) {
          // TC-2 — a 404 is an answer the guest needs to see; a network blip is not.
          if (r.kind === 'not-available' && this.scopeOf(token) === scope) {
            this.ensureDetailNotAvailableSubject(token, itemId).next(true);
          }
          return;
        }
        this.write(this.kDetail(token, itemId, scope), r.data);
        if (this.scopeOf(token) !== scope) return;
        const gone = this.detailNotAvailableState.get(`${token}:${itemId}`);
        if (gone?.value) gone.next(false);
        this.ensureDetailSubject(token, itemId).next(r.data);
      },
      error: () => { this.inFlight.delete(inflightKey); },
    });
  }

  /** TCWARM-1 — categories + items, only while the token's menu is on. */
  private refreshMenu(token: string): void {
    if (!this.menuOpen(token)) return;
    this.refreshCategories(token);
    this.refreshItems(token);
  }

  /** TCWARM-1 — may the token's built-in menu be read? Only once a resolve
   *  (live, or warm from storage) says it is offered: see
   *  {@link builtInMenuOffered}. Pending, error and gone all wait for an
   *  answer that says so. */
  private menuOpen(token: string): boolean {
    const t = this.tokenState.get(token)?.value;
    return t?.status === 'ok' && builtInMenuOffered(t.data);
  }

  // ── Key helpers ────────────────────────────────────────────────
  /** OMNI-2a — F3: the default (null) scope keeps the pre-OMNI in-flight key exactly;
   *  only the delivery scope (flag on) gets its own key. */
  private scopedInflightKey(base: string, scope: GuestMenuFulfilment): string {
    return scope === 'delivery' ? `${base}:delivery` : base;
  }
  private kToken(token: string): string { return `tc.menu.tok.${token}`; }
  // OMNI-2a — the menu layers carry the fulfilment scope (default: the
  // token's current one). The null scope keeps the pre-OMNI key exactly.
  private kCats(token: string, scope = this.scopeOf(token)): string {
    return `tc.menu.cats.${token}${this.kScope(scope)}`;
  }
  private kItems(token: string, scope = this.scopeOf(token)): string {
    return `tc.menu.items.${token}${this.kScope(scope)}`;
  }
  private kDetail(token: string, itemId: string, scope = this.scopeOf(token)): string {
    return `tc.menu.det.${token}.${itemId}${this.kScope(scope)}`;
  }
  private kScope(scope: GuestMenuFulfilment): string {
    return scope === 'delivery' ? '.delivery' : '';
  }

  // ── Shallow equality on the rendered fields ───────────────────
  private categoriesEqual(a: PublicMenuCategory[] | null, b: PublicMenuCategory[]): boolean {
    if (!a || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i].categoryId !== b[i].categoryId || a[i].name !== b[i].name) return false;
    }
    return true;
  }
  private itemsEqual(a: PublicMenuItemSummary[] | null, b: PublicMenuItemSummary[]): boolean {
    if (!a || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const x = a[i], y = b[i];
      if (x.itemId !== y.itemId
          || x.name !== y.name
          || x.price !== y.price
          || x.shortDescription !== y.shortDescription
          || x.imageUrl !== y.imageUrl
          || x.hasOptions !== y.hasOptions
          || x.categoryId !== y.categoryId
          || x.currency !== y.currency) {
        return false;
      }
    }
    return true;
  }

  // ── Storage primitives ─────────────────────────────────────────
  private read<T>(key: string): T | null {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { ts: number; data: T };
      if (!parsed || typeof parsed.ts !== 'number') return null;
      if (Date.now() - parsed.ts > MenuCacheService.TTL_MS) {
        try { sessionStorage.removeItem(key); } catch { /* noop */ }
        return null;
      }
      return parsed.data;
    } catch {
      return null;
    }
  }

  private write<T>(key: string, data: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {
      // quota exceeded, private mode, etc. — cache is best-effort.
    }
  }
}
